/**
 * Shortens the given string such that it's at most n (+1, when ellipsis is used) chars long but words are not cut.
 */
export function shorten(s: string, length: number): string {
  if (!s || s.length <= length) {
    return s;
  }

  let result = '';
  const words = s.split(' ');

  while (result.length + 1 < length) {
    result += ' ' + words.shift();
  }

  if (words.length > 0) {
    result += '…';
  }

  return result.trim();
}

/**
 * Creates a class list out of class names and conditional class names. Conditional class names are added when the
 * first value of the tuple is true.
 *
 * Usage of Conditional vs. truthiness: https://jsben.ch/tFUbT
 * TL;DR: prefer truthiness over Conditionals (5-10% performance improvement).
 *
 * `.trim()` at the end of the string is important for react hydration (e.g. in our CMS).
 * Browsers like to trim attributes while parsing, but react will then warn about hydration differences.
 *
 * @param classNames  list of class names and conditional class names
 * @returns           class list string
 */
export function classList(...classNames: (string | undefined | false | null)[]): string {
  return classNames
    .map(e => {
      if (e === undefined || e === false || e === null || e === '') {
        return '';
      } else {
        return e + ' ';
      }
    })
    .join('')
    .trim();
}

/**
 * Returns the singular string when count is 1, or the plural string otherwise.
 */
export function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

/**
 * Returns the text of given HTML string.
 *
 * For browser with DOMParser support this function parses the given HTML into proper DOM and uses innerText to return
 * the shown text. On older browsers (supposedly Android 4.3 and earlier, but 4.4 also fails here in our case) we remove
 * tags with an regex which *technically* should provide the same solution.
 *
 * @param html  an HTML string
 */
export const stripHTML = (html: string): string => {
  try {
    return new DOMParser().parseFromString(html, 'text/html').body.innerText;
  } catch (e) {
    return html.replace(/(<([^>]+)>)/gi, '');
  }
};

/**
 * Escapes < and > chars with HTML equivalents.
 *
 * @param html  an HTML string
 */
export const escapeHTML = (html: string): string => {
  return html.replace(/</g, '&lt;').replace(/>/g, '&gt;');
};

export const escapeMetaDescription = (html: string): string => {
  return html.replace(/"/g, '&quot;');
};

/**
 * Returns whether the string is an emoji.
 *
 * @see https://stackoverflow.com/questions/18862256/how-to-detect-emoji-using-javascript
 */
export const isEmoji = (text: string): boolean => {
  return (
    text.length === 2 &&
    !!text.match(
      /(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|[\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|[\ud83c[\ude32-\ude3a]|[\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])/g
    )
  );
};

/**
 * Formats an integer in German localization (decimal points).
 */
export const formatInt = (n: number): string => n.toLocaleString('de-DE');

/**
 * Capitalizes the first letter of the given string.
 */
export const capitalize = (text: string): string => {
  if (!text.length) {
    return text;
  }

  return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
};

/**
 * Make the first character in a string lower case.
 */
export const decapitalize = (text: string): string => {
  if (!text.length) {
    return text;
  }

  return `${text.charAt(0).toLowerCase()}${text.slice(1)}`;
};

export const capitalizeWords = (text: string): string =>
  text
    .split(' ')
    .map(part => capitalize(part))
    .join(' ');

/**
 * Simple, zero security hashing for string, based on Java's hashCode.
 *
 * @see https://stackoverflow.com/questions/6122571/simple-non-secure-hash-function-for-javascript
 */
export const unsecureHash = (text: string): number => {
  let hash = 0;
  if (text.length === 0) {
    return hash;
  }
  for (let i = 0; i < text.length; i++) {
    var char = text.charCodeAt(i);
    // eslint-disable-next-line no-bitwise
    hash = (hash << 5) - hash + char;
    // eslint-disable-next-line no-bitwise
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash;
};

/**
 * Removes "Zalgo" text formatting from given string.
 *
 * See https://en.wikipedia.org/wiki/Zalgo_text
 *
 * @param text              user input to be cleaned
 * @param maxCombiningMarks maximum amount of combining marks allowed per char. defaults to 3 to allow sane usage.
 */
export const unzalgoText = (text: string, maxCombiningMarks: number = 3): string => {
  // these ranges are taken from https://github.com/mathiasbynens/strip-combining-marks/blob/master/strip-combining-marks.js
  // additional ranges have been added!
  const regexSymbolWithCombiningMarks =
    /([\0-\u02FF\u0370-\u1AAF\u1B00-\u1DBF\u1E00-\u20CF\u2100-\uD7FF\uE000-\uFE1F\uFE30-\uFFFF]|[\uD800-\uDBFF][\uDC00-\uDFFF]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?:[^\uD800-\uDBFF]|^)[\uDC00-\uDFFF])([\u0300-\u036F\u1AB0-\u1AFF\u1DC0-\u1DFF\u20D0-\u20FF\uFE20-\uFE2F]+)/g;
  const regexLineBreakCombiningMarks =
    /[\0-\x08\x0E-\x1F\x7F-\x84\x86-\x9F\u0300-\u034E\u0350-\u035E\u0360-\u036F\u0483-\u0489\u0591-\u05BD\u05BF\u05C1\u05C2\u05C4\u05C5\u05C7\u0610-\u061A\u061C\u064B-\u065F\u0670\u06D6-\u06DC\u06DF-\u06E4\u06E7\u06E8\u06EA-\u06ED\u0711\u0730-\u074A\u07A6-\u07B0\u07EB-\u07F3\u0816-\u0819\u081B-\u0823\u0825-\u0827\u0829-\u082D\u0859-\u085B\u08D4-\u08E1\u08E3-\u0903\u093A-\u093C\u093E-\u094F\u0951-\u0957\u0962\u0963\u0981-\u0983\u09BC\u09BE-\u09C4\u09C7\u09C8\u09CB-\u09CD\u09D7\u09E2\u09E3\u0A01-\u0A03\u0A3C\u0A3E-\u0A42\u0A47\u0A48\u0A4B-\u0A4D\u0A51\u0A70\u0A71\u0A75\u0A81-\u0A83\u0ABC\u0ABE-\u0AC5\u0AC7-\u0AC9\u0ACB-\u0ACD\u0AE2\u0AE3\u0B01-\u0B03\u0B3C\u0B3E-\u0B44\u0B47\u0B48\u0B4B-\u0B4D\u0B56\u0B57\u0B62\u0B63\u0B82\u0BBE-\u0BC2\u0BC6-\u0BC8\u0BCA-\u0BCD\u0BD7\u0C00-\u0C03\u0C3E-\u0C44\u0C46-\u0C48\u0C4A-\u0C4D\u0C55\u0C56\u0C62\u0C63\u0C81-\u0C83\u0CBC\u0CBE-\u0CC4\u0CC6-\u0CC8\u0CCA-\u0CCD\u0CD5\u0CD6\u0CE2\u0CE3\u0D01-\u0D03\u0D3E-\u0D44\u0D46-\u0D48\u0D4A-\u0D4D\u0D57\u0D62\u0D63\u0D82\u0D83\u0DCA\u0DCF-\u0DD4\u0DD6\u0DD8-\u0DDF\u0DF2\u0DF3\u0F18\u0F19\u0F35\u0F37\u0F39\u0F3E\u0F3F\u0F71-\u0F7E\u0F80-\u0F84\u0F86\u0F87\u0F8D-\u0F97\u0F99-\u0FBC\u0FC6\u135D-\u135F\u1712-\u1714\u1732-\u1734\u1752\u1753\u1772\u1773\u180B-\u180D\u1885\u1886\u18A9\u1920-\u192B\u1930-\u193B\u1A17-\u1A1B\u1A7F\u1AB0-\u1ABE\u1B00-\u1B04\u1B34-\u1B44\u1B6B-\u1B73\u1B80-\u1B82\u1BA1-\u1BAD\u1BE6-\u1BF3\u1C24-\u1C37\u1CD0-\u1CD2\u1CD4-\u1CE8\u1CED\u1CF2-\u1CF4\u1CF8\u1CF9\u1DC0-\u1DF5\u1DFB-\u1DFF\u200C\u200E\u200F\u202A-\u202E\u2066-\u206F\u20D0-\u20F0\u2CEF-\u2CF1\u2D7F\u2DE0-\u2DFF\u302A-\u302F\u3035\u3099\u309A\uA66F-\uA672\uA674-\uA67D\uA69E\uA69F\uA6F0\uA6F1\uA802\uA806\uA80B\uA823-\uA827\uA880\uA881\uA8B4-\uA8C5\uA8E0-\uA8F1\uA926-\uA92D\uA947-\uA953\uA980-\uA983\uA9B3-\uA9C0\uAA29-\uAA36\uAA43\uAA4C\uAA4D\uAAEB-\uAAEF\uAAF5\uAAF6\uABE3-\uABEA\uABEC\uABED\uFB1E\uFE00-\uFE0F\uFE20-\uFE2F\uFFF9-\uFFFB]|\uD800[\uDDFD\uDEE0\uDF76-\uDF7A]|\uD802[\uDE01-\uDE03\uDE05\uDE06\uDE0C-\uDE0F\uDE38-\uDE3A\uDE3F\uDEE5\uDEE6]|\uD804[\uDC00-\uDC02\uDC38-\uDC46\uDC7F-\uDC82\uDCB0-\uDCBA\uDD00-\uDD02\uDD27-\uDD34\uDD73\uDD80-\uDD82\uDDB3-\uDDC0\uDDCA-\uDDCC\uDE2C-\uDE37\uDE3E\uDEDF-\uDEEA\uDF00-\uDF03\uDF3C\uDF3E-\uDF44\uDF47\uDF48\uDF4B-\uDF4D\uDF57\uDF62\uDF63\uDF66-\uDF6C\uDF70-\uDF74]|\uD805[\uDC35-\uDC46\uDCB0-\uDCC3\uDDAF-\uDDB5\uDDB8-\uDDC0\uDDDC\uDDDD\uDE30-\uDE40\uDEAB-\uDEB7]|\uD807[\uDC2F-\uDC36\uDC38-\uDC3F\uDC92-\uDCA7\uDCA9-\uDCB6]|\uD81A[\uDEF0-\uDEF4\uDF30-\uDF36]|\uD81B[\uDF51-\uDF7E\uDF8F-\uDF92]|\uD82F[\uDC9D\uDC9E\uDCA0-\uDCA3]|\uD834[\uDD65-\uDD69\uDD6D-\uDD82\uDD85-\uDD8B\uDDAA-\uDDAD\uDE42-\uDE44]|\uD836[\uDE00-\uDE36\uDE3B-\uDE6C\uDE75\uDE84\uDE9B-\uDE9F\uDEA1-\uDEAF]|\uD838[\uDC00-\uDC06\uDC08-\uDC18\uDC1B-\uDC21\uDC23\uDC24\uDC26-\uDC2A]|\uD83A[\uDCD0-\uDCD6\uDD44-\uDD4A]|\uDB40[\uDC01\uDC20-\uDC7F\uDD00-\uDDEF]/g;

  return [...text.normalize()].reduce<[string, number]>(
    ([text, currentCombiningMarkSequenceLength], char) => {
      const isCombiningMark =
        !!char.match(regexLineBreakCombiningMarks) || !!char.match(regexSymbolWithCombiningMarks);
      const shownChar =
        !isCombiningMark || currentCombiningMarkSequenceLength < maxCombiningMarks ? char : '';

      return [text + shownChar, isCombiningMark ? currentCombiningMarkSequenceLength + 1 : 0];
    },
    ['', 0]
  )[0];
};

export const stringToBooleanOpt = (value: string | undefined | null): boolean | undefined => {
  return value === 'true' ? true : value === 'false' ? false : undefined;
};

export const formatPrice = (price: number, fractionDigits: number | undefined = 2): string => {
  return price.toFixed(fractionDigits).replace('.', ',');
};

export const cssPseudoString = (pseudo: string): string => {
  return pseudo.replace(/\ /g, '\\00a0');
};
